// @flow

import * as React from "react";
import { parse, isPast } from "date-fns";

import { type ImageType, type LinkType } from "../../types";

import { ReactComponent as Triangle } from "../../../assets/images/shape-triangle.svg";
import { ReactComponent as Circle } from "../../../assets/images/shape-circle.svg";
import { ReactComponent as RingMobile } from "../../../assets/images/shape-ring-thick-fill.svg";
import { ReactComponent as Ring } from "../../../assets/images/shape-ring-detail-fill.svg";
import Breadcrumbs from "../molecules/Breadcrumbs";
import MediaGallery from "./MediaGallery";
import { PrimaryNavPad } from "./PrimaryNav";
import TextButton from "../atoms/TextButton";
import { getEventDateString } from "../../utils/date";
import AddToCalendarModal from "../organisms/AddToCalendarModal";
import Pill from "../atoms/Pill";
import LinkHandler from "../atoms/LinkHandler";

type PropsType = {
    theme: "purple" | "orange" | "green" | "teal" | "pink" | "red" | "blue",
    logo: ImageType,
    title: string,
    description?: string,
    location_google_map_link: string,
    start_date: string,
    end_date: string,
    location: string,
    breadcrumbs: any,
    parent_event: any,
    sold_out: boolean,
    status?: string,
    media: {
        image: ImageType,
        youtube_url: LinkType,
        caption?: string,
    }[],
    full_width?: boolean,
};

const HeroEventDetail = (props: PropsType): React.Node => {
    const [showCalendarModal, setShowCalendarModal] = React.useState(false);

    const baseClassName = "hero-event-detail";
    const classes = [baseClassName];
    const dates = {
        startsAt: parse(props.start_date, "dd/MM/yyyy h:mm aa", new Date()),
        endsAt: parse(props.end_date, "dd/MM/yyyy h:mm aa", new Date()),
    };

    if (props.theme) {
        classes.push(`${baseClassName}--${props.theme}`);
    }

    if (props.full_width) {
        classes.push(`${baseClassName}--full-width`);
    }

    return (
        <div className={classes.join(" ")}>
            <div className="hero-event-detail__inner">
                <PrimaryNavPad />
                <div className="hero-event-detail__container container-fluid">
                    {/* Design elements */}
                    <Circle className="hero-event-detail__circle" />
                    <Ring className="hero-event-detail__ring" />
                    <RingMobile className="hero-event-detail__ring-mobile" />
                    <Triangle className="hero-event-detail__triangle" />

                    <div className="hero-event-detail__row row">
                        <div
                            className={`col-xs-12 ${
                                !props.full_width
                                    ? "col-sm-6 col-md-7"
                                    : "last-xs"
                            } hero-event-detail__left-content`}
                        >
                            <Breadcrumbs breadcrumbs={props.breadcrumbs} />
                            <MediaGallery
                                mode="header"
                                media={props.media}
                                logo={props.logo}
                                theme={props.theme}
                            />
                        </div>
                        <div
                            className={`col-xs-12 ${
                                !props.full_width
                                    ? "col-sm-6 col-md-5 col-lg-4 col-lg-offset-1"
                                    : ""
                            }`}
                        >
                            <div className="hero-event-detail__right-content">
                                <div className="row">
                                    <div
                                        className={`${
                                            props.full_width
                                                ? "col-xs-12 col-md-6 col-lg-8"
                                                : "col-xs-12"
                                        } `}
                                    >
                                        <Breadcrumbs
                                            breadcrumbs={props.breadcrumbs}
                                        />
                                        {props.status === "cancelled" ? (
                                            <div className="hero-event-detail__status">
                                                <Pill color="error">
                                                    Event has been cancelled
                                                </Pill>
                                            </div>
                                        ) : isPast(dates.endsAt) ? (
                                            <div className="hero-event-detail__status">
                                                <Pill color="error">
                                                    Event has finished
                                                </Pill>
                                            </div>
                                        ) : props.sold_out ? (
                                            <div className="hero-event-detail__status">
                                                <Pill color="stain" inverted>
                                                    Tickets have sold out
                                                </Pill>
                                            </div>
                                        ) : null}

                                        <h2 className="hero-event-detail__title">
                                            {props.title}
                                        </h2>

                                        {props.parent_event ? (
                                            <div className="hero-event-detail__parent-link">
                                                Part of{" "}
                                                <LinkHandler
                                                    to={props.parent_event.url}
                                                >
                                                    {props.parent_event.title}
                                                </LinkHandler>
                                            </div>
                                        ) : null}

                                        {props.description ? (
                                            <p className="hero-event-detail__description">
                                                {props.description}
                                            </p>
                                        ) : null}
                                    </div>

                                    <div
                                        className={`${
                                            props.full_width
                                                ? "hero-event-detail__footer col-xs-12 col-md-6 col-lg-4"
                                                : "hero-event-detail__footer col-xs-12"
                                        } `}
                                    >
                                        <div className="hero-event-detail__footer__left-content">
                                            <div className="hero-event-detail__footer__title">
                                                When
                                            </div>
                                            <div className="hero-event-detail__footer__desc">
                                                {getEventDateString(
                                                    props.start_date,
                                                    props.end_date
                                                ).map((date, key) => (
                                                    <div key={key}>{date}</div>
                                                ))}
                                            </div>
                                            <TextButton
                                                onClick={() =>
                                                    setShowCalendarModal(true)
                                                }
                                            >
                                                Add to calendar
                                            </TextButton>
                                        </div>
                                        <div className="hero-event-detail__footer__right-content">
                                            <div className="hero-event-detail__footer__title">
                                                Where
                                            </div>
                                            <div className="hero-event-detail__footer__desc">
                                                {props.location}
                                            </div>
                                            {props.location_google_map_link ? (
                                                <TextButton
                                                    to={
                                                        props.location_google_map_link
                                                    }
                                                    target="blank"
                                                >
                                                    View map
                                                </TextButton>
                                            ) : null}
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
            <AddToCalendarModal
                onClose={() => setShowCalendarModal(false)}
                calendarDetails={{
                    title: props.title,
                    location: props.location,
                    description: props.description,
                    start: dates.startsAt,
                    end: dates.endsAt,
                }}
                active={showCalendarModal}
            />
        </div>
    );
};

HeroEventDetail.defaultProps = {
    theme: "purple",
};

export default HeroEventDetail;
